import type { FC } from 'react';
import { Link } from 'react-router';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import {
  getArtifactDisplayTitle,
  getArtifactStatusLabel,
  getArtifactStatusTone,
  getArtifactTypeIcon,
  getArtifactTypeLabel,
  resolveArtifactUrl,
  shouldDisplayArtifactStatus,
} from '@gonfalon/observability-ui/components/Artifact';
import type { ArtifactStatusTone } from '@gonfalon/observability-ui/components/Artifact';
import { Icon } from '@launchpad-ui/icons';

import { flagOverallStatusTone } from '../../../../internal/components/AgentInsights/components/artifacts/deriveFlagEnvSummaries';
import { FlagEnvironmentList } from '../../../../internal/components/AgentInsights/components/artifacts/FlagEnvironmentList';
import {
  isFlagArtifactType,
  isPullRequestArtifactType,
} from '../../../../internal/components/AgentInsights/components/artifacts/parseArtifactIdentity';
import { formatRelativeDate } from '../../../../utils/formatRelativeDate';

import type { ArtifactCardLiveData, ArtifactCardStoredMeta } from './useArtifactCardData';
import { useArtifactCardData } from './useArtifactCardData';

import * as styles from '../../../../internal/components/AgentInsights/components/artifacts/ArtifactCard.css';

export type { ArtifactCardLiveData, ArtifactCardStoredMeta } from './useArtifactCardData';
export type { ArtifactCardFlagData } from './useArtifactCardData';
export { FlagEnvironmentList } from '../../../../internal/components/AgentInsights/components/artifacts/FlagEnvironmentList';
export { useFlagArtifactData } from './useArtifactCardData';

const buildArtifactPath = (projectKey: string, type: string, externalId: string): string =>
  `/projects/${encodeURIComponent(projectKey)}/vega/artifacts/${encodeURIComponent(type)}/${encodeURIComponent(externalId)}`;

type StatusTone = ArtifactStatusTone | 'good' | 'bad' | 'caution';

const FLAG_OVERALL_LABELS = new Set(['On', 'Off', 'Rolling out', 'Regressed', 'Mixed']);

function toneForLabel(label: string | undefined): StatusTone {
  if (!label) {
    return 'neutral';
  }
  if (FLAG_OVERALL_LABELS.has(label)) {
    return flagOverallStatusTone(label);
  }
  const normalized = label.trim().toLowerCase();
  if (normalized === 'open') {
    return 'open';
  }
  if (normalized === 'merged') {
    return 'merged';
  }
  if (normalized === 'closed') {
    return 'closed';
  }
  if (normalized === 'draft') {
    return 'draft';
  }
  if (
    normalized === 'healthy' ||
    normalized === 'normal' ||
    normalized === 'enabled' ||
    normalized === 'completed' ||
    normalized === 'rolling out'
  ) {
    return 'good';
  }
  if (normalized === 'alerting' || normalized === 'regressed' || normalized === 'error') {
    return 'bad';
  }
  if (normalized === 'warning' || normalized === 'off' || normalized === 'disabled') {
    return 'caution';
  }
  return getArtifactStatusTone(label);
}

function formatUpdated(raw: string | number | null | undefined, now: Date): string | null {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  const asString = typeof raw === 'number' ? new Date(raw).toISOString() : String(raw);
  const date = new Date(asString);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return `Updated ${formatRelativeDate(asString, now)}`;
}

function formatAlertState(state: string | undefined, enabled: boolean | undefined): string | undefined {
  if (enabled === false) {
    return 'Disabled';
  }
  if (!state) {
    return enabled === true ? 'Enabled' : undefined;
  }
  if (state === 'Normal') {
    return 'Healthy';
  }
  if (state === 'Alerting' || state === 'AlertingSilently') {
    return 'Alerting';
  }
  return state;
}

function formatAlertStateFact(state: string | undefined): string {
  if (state === 'Normal') {
    return ' · No recent alerts';
  }
  if (state === 'Alerting' || state === 'AlertingSilently') {
    return ' · Alerting';
  }
  if (state) {
    return ` · ${state}`;
  }
  // Unknown evaluation state: don't imply the alert is healthy.
  return '';
}

function externalLinkLabel(type: string, typeLabel: string): string {
  if (isPullRequestArtifactType(type)) {
    return msg('vega.artifactCard.openPullRequestLabel');
  }
  if (isFlagArtifactType(type)) {
    return msg('vega.artifactCard.openFlagLabel');
  }
  return msg('vega.artifactCard.openExternallyLabel', { typeLabel: typeLabel.toLowerCase() });
}

export interface ArtifactCardViewProps {
  projectKey: string;
  artifact: ArtifactCardStoredMeta;
  live: ArtifactCardLiveData;
  /** Clock for relative timestamps. Defaults to `Date.now()`. */
  now?: number;
}

const PullRequestFacts: FC<{
  live: Extract<ArtifactCardLiveData, { kind: 'pr' }>;
}> = ({ live }) => {
  const hasChecks = Boolean(live.checks && live.checks.total > 0);
  const approvals = live.approvals ?? 0;
  const changesRequested = live.changesRequested ?? 0;
  const hasReviews = approvals > 0 || changesRequested > 0;
  if (!live.repoLabel && !hasChecks && !hasReviews) {
    return null;
  }

  const checkTone =
    live.checks && live.checks.failure > 0
      ? styles.factBad
      : live.checks && live.checks.pending > 0
        ? styles.factCaution
        : styles.factGood;

  return (
    <>
      {live.repoLabel ? (
        <div className={styles.facts}>
          <span className={styles.factStrong}>{live.repoLabel}</span>
        </div>
      ) : null}
      {hasChecks || hasReviews ? (
        <div className={styles.facts}>
          {hasChecks && live.checks ? (
            <span>
              <Message
                id="vega.artifactCard.checks"
                values={{
                  success: live.checks.success,
                  total: live.checks.total,
                  strong: (chunks) => <span className={`${styles.factStrong} ${checkTone}`}>{chunks}</span>,
                }}
              />
              {live.checks.failure > 0 ? (
                <span className={styles.factBad}>
                  {' '}
                  <Message id="vega.artifactCard.checksFailing" values={{ count: live.checks.failure }} />
                </span>
              ) : null}
              {live.checks.pending > 0 ? (
                <span className={styles.factCaution}>
                  {' '}
                  <Message id="vega.artifactCard.checksPending" values={{ count: live.checks.pending }} />
                </span>
              ) : null}
            </span>
          ) : null}
          {hasReviews ? (
            <span>
              <Message
                id="vega.artifactCard.reviews"
                values={{
                  summary:
                    changesRequested > 0
                      ? msg('vega.artifactCard.changesRequested', { count: changesRequested })
                      : msg('vega.artifactCard.approved', { count: approvals }),
                  strong: (chunks) => <span className={styles.factStrong}>{chunks}</span>,
                }}
              />
              {changesRequested > 0 && approvals > 0 ? (
                <span>
                  {' '}
                  <Message id="vega.artifactCard.alsoApproved" values={{ count: approvals }} />
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

/**
 * Presentational rich artifact card. Prefer `ArtifactCard` in app surfaces so live
 * data is fetched; use this directly in Storybook/tests with injected `live` data.
 */
export const ArtifactCardView: FC<ArtifactCardViewProps> = ({ projectKey, artifact, live, now }) => {
  const clock = now === undefined ? new Date() : new Date(now);
  const typeLabel = getArtifactTypeLabel(artifact.type);
  const icon = getArtifactTypeIcon(artifact.type);

  const liveTitle = live.kind === 'pr' ? live.liveTitle : live.kind === 'alert' ? live.liveName : null;
  const displayTitle = getArtifactDisplayTitle(artifact.type, liveTitle ?? artifact.title, artifact.externalId);

  const liveUrl = live.kind === 'pr' ? live.liveUrl : null;
  const safeUrl = resolveArtifactUrl(artifact.type, liveUrl ?? artifact.url);

  let statusLabel: string | undefined;
  if (live.kind === 'pr' && live.stateLabel) {
    statusLabel = getArtifactStatusLabel(live.stateLabel);
  } else if (live.kind === 'flag' && !live.unavailable && live.overallLabel) {
    statusLabel = live.overallLabel;
  } else if (live.kind === 'alert' && !live.unavailable) {
    statusLabel = formatAlertState(typeof live.state === 'string' ? live.state : undefined, live.enabled);
  }

  // When live GitHub summary is unavailable, fall back to the artifact graph's
  // last-known PR status (webhook/tool writes). Do not invent checks or diffs.
  if (
    !statusLabel &&
    live.kind === 'pr' &&
    live.unavailable &&
    shouldDisplayArtifactStatus(artifact.type, artifact.status) &&
    artifact.status
  ) {
    statusLabel = getArtifactStatusLabel(artifact.status);
  }

  const statusTone = toneForLabel(statusLabel);
  // PR cards prefer live GitHub updated_at. Alert evaluation time belongs in the
  // body facts line — using it here would duplicate as a mislabeled "Updated …".
  const updatedAt = live.kind === 'pr' && live.liveUpdatedAt != null ? live.liveUpdatedAt : artifact.updatedAt;
  const updatedLabel = formatUpdated(updatedAt, clock);
  const evaluatedLabel =
    live.kind === 'alert' && live.lastEvaluatedAt != null
      ? (() => {
          const relative = formatUpdated(live.lastEvaluatedAt, clock);
          return relative ? relative.replace(/^Updated /, 'Last evaluated ') : null;
        })()
      : null;

  const prFacts = !live.loading && live.kind === 'pr' && !live.unavailable ? <PullRequestFacts live={live} /> : null;
  const flagEnvs =
    !live.loading && live.kind === 'flag' && !live.unavailable && live.environments.length > 0 ? (
      <FlagEnvironmentList environments={live.environments} density="card" />
    ) : null;
  const alertFacts =
    !live.loading && live.kind === 'alert' && !live.unavailable ? (
      <div className={styles.facts}>
        <span>
          {live.enabled === false ? 'Disabled' : 'Enabled'}
          {formatAlertStateFact(typeof live.state === 'string' ? live.state : undefined)}
        </span>
        {evaluatedLabel ? <span>{evaluatedLabel}</span> : null}
      </div>
    ) : null;
  const hasBody = Boolean(live.loading || prFacts || flagEnvs || alertFacts);

  return (
    <div className={styles.cardShell}>
      <Link
        to={buildArtifactPath(projectKey, artifact.type, artifact.externalId)}
        className={styles.card}
        aria-label={`${typeLabel}: ${displayTitle}`}
      >
        <div className={styles.header}>
          <div className={styles.headerMain}>
            <div className={styles.typeRow}>
              <Icon name={icon} size="small" className={styles.typeIcon} />
              <span className={styles.typeLabel}>{typeLabel}</span>
              {statusLabel ? (
                <span className={`${styles.status} ${styles.statusTone[statusTone]}`}>{statusLabel}</span>
              ) : null}
            </div>
            {live.loading ? <div className={styles.skeletonTitle} /> : <h3 className={styles.title}>{displayTitle}</h3>}
          </div>
        </div>

        {hasBody ? (
          <div className={styles.body}>
            {live.loading ? (
              <>
                <div className={styles.skeletonFact} />
                <div className={styles.skeletonRow} />
              </>
            ) : null}
            {prFacts}
            {flagEnvs}
            {alertFacts}
          </div>
        ) : null}

        {updatedLabel ? (
          <div className={styles.footer}>
            <span className={styles.timestamp}>{updatedLabel}</span>
          </div>
        ) : null}
      </Link>
      {safeUrl ? (
        <a
          href={safeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.externalLink}
          aria-label={externalLinkLabel(artifact.type, typeLabel)}
          title={msg('vega.artifactCard.openExternallyTitle', { typeLabel: typeLabel.toLowerCase() })}
        >
          <Icon name="link-external" size="small" className={styles.typeIcon} />
        </a>
      ) : null}
    </div>
  );
};

export interface ArtifactCardProps {
  projectId: string;
  projectKey: string;
  artifact: ArtifactCardStoredMeta;
}

/** Rich artifact card that fetches live PR/flag/alert data for the mounted row. */
export const ArtifactCard: FC<ArtifactCardProps> = ({ projectId, projectKey, artifact }) => {
  const live = useArtifactCardData({ projectId, projectKey, artifact });
  return <ArtifactCardView projectKey={projectKey} artifact={artifact} live={live} />;
};
